import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import WrapPage from '@/components/base/Layout/wrap';
import { useCDN } from '@/utils/cdn';
import styled from 'styled-components';
import useLanguage from '@/hooks/useLanguage';
import Linker from '@/components/base/Linker';
import MasterBlockListTable from '@/components/combined/MasterBlockListTable';
import MasterTransactionListTable from '@/components/combined/MasterTransactionListTable';
import getOverviewService from '@/api/overview';
import refreshStore from '@/store/Refresh';
import { prettyNumber, prettySize, toDio, toThroughput, toShardCount, toTxnUnit } from '@/utils/string';
import { observer } from 'mobx-react-lite';
import Tooltip from '@/components/base/Tooltip';
var HomeTotalStyled = styled.div.withConfig({
    displayName: "pages__HomeTotalStyled",
    componentId: "sc-1sdbcp3-0"
})([
    "",
    " .item{",
    " &:not(:first-child){",
    " &::after{content:'';",
    "}}img{",
    "}.text{",
    "}.number{",
    "}.title{",
    "}}"
], {
    "backgroundColor": "var(--color-box-bg)",
    "borderRadius": "6px",
    "width": "100%",
    "height": "168px",
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "space-around",
    "marginBottom": "16px"
}, {
    "display": "inline-block"
}, {
    "position": "relative",
    "height": "100%",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center"
}, {
    "width": "1px",
    "height": "88px",
    "position": "absolute",
    "left": "-49px",
    "top": "40px",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgba(229, 230, 235, var(--tw-bg-opacity))"
}, {
    "display": "inline-block",
    "width": "92px",
    "height": "106px",
    "marginRight": "24px"
}, {
    "display": "inline-block",
    "verticalAlign": "middle",
    "maxWidth": "180px"
}, {
    "fontSize": "32px",
    "marginBottom": "12px",
    "lineHeight": "37px"
}, {
    "fontSize": "16px",
    "color": "var(--color-desc-text)",
    "marginTop": "0px",
    "marginBottom": "0px"
});
var HomeInfoStyled = styled.div.withConfig({
    displayName: "pages__HomeInfoStyled",
    componentId: "sc-1sdbcp3-1"
})([
    "",
    " .item{",
    " p,p > span{",
    "}p + p{",
    "}}"
], {
    "width": "100%",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "space-between"
}, {
    "width": "210px",
    "height": "120px",
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "flexDirection": "column",
    "textAlign": "center",
    "backgroundColor": "var(--color-box-bg)",
    "borderRadius": "6px"
}, {
    "fontSize": "26px",
    "lineHeight": "30px",
    "marginBottom": "8px",
    "fontWeight": "700"
}, {
    "fontWeight": "400",
    "marginBottom": "0px",
    "color": "var(--color-desc-text)",
    "fontSize": "14px",
    "lineHeight": "17px"
});
var overviewService = getOverviewService();
var Home = function() {
    var translate = useLanguage().translate;
    var ref1 = useState(null), summary = ref1[0], setSummary = ref1[1];
    var refresh = refreshStore.refresh;
    var pageLoaded = useRef(false);
    useEffect(function() {
        if (refresh) {
            fetchOverview();
        }
    }, [
        refresh
    ]);
    var total = useMemo(function() {
        return [
            {
                img: '/img/overview1.png',
                title: 'Total blocks',
                key: 'TotalBlocks',
                render: function(data) {
                    return prettyNumber(data);
                }
            },
            {
                img: '/img/overview2.png',
                title: 'Mempool',
                key: 'MempoolSize',
                render: function(data) {
                    return prettyNumber(data);
                }
            },
            {
                img: '/img/overview3.png',
                title: 'Difficulty',
                key: 'Difficulty',
                render: function(data) {
                    return prettyNumber(data);
                }
            },
            {
                img: '/img/overview4.png',
                title: 'Med Gas Price',
                key: 'AvgGasPrice',
                render: function(data) {
                    return toDio(data);
                }
            }
        ];
    }, []);
    var sumItems = useMemo(function() {
        return [
            {
                title: 'Shard Number',
                key: 'ShardOrder',
                render: function(data) {
                    return data ? toShardCount(data) : 0;
                }
            },
            {
                title: 'Transactions',
                key: 'TotalTxn',
                render: function(data) {
                    return toTxnUnit(data || 0);
                }
            },
            {
                title: 'Avg block time',
                key: 'BlockInterval',
                render: function(data) {
                    return(/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            data ? Number(data.toFixed(2)) : 0,
                            " ",
                            /*#__PURE__*/ _jsx("span", {
                                children: "s"
                            })
                        ]
                    }));
                }
            },
            {
                title: 'State size',
                key: 'TotalStateSize',
                render: function(data) {
                    var ref = prettySize(data), value = ref.value, unit = ref.unit;
                    return(/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            value || 0,
                            " ",
                            /*#__PURE__*/ _jsx("span", {
                                children: unit
                            })
                        ]
                    }));
                }
            },
            {
                title: 'Throughput',
                key: 'Throughput',
                render: function(data) {
                    return(/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            data || 0,
                            " ",
                            /*#__PURE__*/ _jsx("span", {
                                children: toThroughput('')
                            })
                        ]
                    }));
                }
            },
            {
                title: 'Fork rate',
                key: 'ForkRate',
                render: function(data) {
                    return(/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            data || 0,
                            " ",
                            /*#__PURE__*/ _jsx("span", {
                                children: "%"
                            })
                        ]
                    }));
                }
            }
        ];
    }, []);
    var fetchOverview = useCallback(function() {
        overviewService.getOverview().then(function(response) {
            pageLoaded.current = true;
            var data = response.Result;
            if (data === null || data === void 0 ? void 0 : data.BlockInterval) {
                data.BlockInterval = data.BlockInterval / 1000;
            }
            setSummary(data);
        });
    }, []);
    useEffect(function() {
        return fetchOverview();
    }, []);
    return(/*#__PURE__*/ _jsxs("div", {
        className: "container-w mx-auto",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[20px] mb-[31px] rounded-[6px]",
                children: [
                    /*#__PURE__*/ _jsx(HomeTotalStyled, {
                        children: total.map(function(item) {
                            /*#__PURE__*/ return _jsxs("div", {
                                className: "item",
                                children: [
                                    /*#__PURE__*/ _jsx("img", {
                                        src: useCDN(item.img),
                                        alt: item.title
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text",
                                        children: [
                                            /*#__PURE__*/ _jsx(Tooltip, {
                                                placement: "top",
                                                overlay: pageLoaded.current && summary ? item.render ? item.render(summary[item.key]) : summary[item.key] : '--',
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    className: "number font-inter-semibold line-clamp",
                                                    children: pageLoaded.current && summary ? item.render ? item.render(summary[item.key]) : summary[item.key] : '--'
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "title",
                                                children: translate(item.title)
                                            })
                                        ]
                                    })
                                ]
                            }, item.title);
                        })
                    }),
                    /*#__PURE__*/ _jsx(HomeInfoStyled, {
                        children: sumItems.map(function(item) {
                            return item.link ? /*#__PURE__*/ _jsx(Linker, {
                                href: item.link,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "item",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: pageLoaded.current && summary ? item.render ? item.render(summary[item.key]) : summary[item.key] : '--'
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            children: translate(item.title)
                                        })
                                    ]
                                }, item.title)
                            }, item.title) : /*#__PURE__*/ _jsxs("div", {
                                className: "item",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: pageLoaded.current && summary ? item.render ? item.render(summary[item.key]) : summary[item.key] : '--'
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: translate(item.title)
                                    })
                                ]
                            }, item.title);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start mb-[64px] justify-between",
                children: [
                    /*#__PURE__*/ _jsx(MasterBlockListTable, {
                        className: "w-[670px] h-[780px]",
                        title: translate('Recent Blocks')
                    }),
                    /*#__PURE__*/ _jsx(MasterTransactionListTable, {
                        className: "w-[670px] h-[780px]",
                        title: translate('Recent transactions')
                    })
                ]
            })
        ]
    }));
};
export default WrapPage(observer(Home));
